import { showUserAlert } from "../utils/userErrorMessage";
// CarEditPage.jsx
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../supabase/client";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import Header from "../Component/Header";
import { syncDriverProfileFromCar } from "../utils/driverCarSync";

export default function CarEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    car_number: "",
    car_name: "",
    driver: "",
    driver_email: "",
    phone: "",
    year: "",
    seat: "",
    seat_detail: "",
    union_fee: "",
    career: "",
    karaoke_machine: false,
    electronic_display: false,
  });

  // 🔎 미배정 기사 목록 상태
  const [driverCandidates, setDriverCandidates] = useState([]);
  const [showDriverList, setShowDriverList] = useState(false);
  const [driverQuery, setDriverQuery] = useState("");
  const driverInputRef = useRef(null);
  const driverListWrapRef = useRef(null);

  // 🔹 이전 드라이버 정보 보관
  const [prevDriverEmail, setPrevDriverEmail] = useState(null);
  const [prevDriverName, setPrevDriverName] = useState("");
  const [previousCarForSync, setPreviousCarForSync] = useState(null);

  useEffect(() => {
    const fetchCar = async () => {
      const company = localStorage.getItem("email") || "";
      if (!company) return;
      const { data, error } = await supabase
        .from("cars")
        .select("*")
        .eq("id", id)
        .eq("company", company)
        .single();
      if (!error && data) {
        setForm({
          car_number: data.car_number,
          car_name: data.car_name || "",
          driver: data.driver || "",
          driver_email: data.driver_email || "",
          phone: data.phone || "",
          year: data.year || "",
          seat: data.seat || "",
          seat_detail: data.seat_detail || "",
          union_fee: data.union_fee || "",
          career: data.career || "",
          karaoke_machine: !!data.karaoke_machine,
          electronic_display: !!data.electronic_display,
        });
        setPrevDriverEmail(data.driver_email || null); // 🔹 이전 기사 이메일 저장
        setPrevDriverName(data.driver || "");
        setPreviousCarForSync({
          id: data.id,
          car_number: data.car_number,
          car_number_only: data.car_number_only,
          driver: data.driver || "",
          driver_email: data.driver_email || "",
          phone: data.phone || "",
        });
      }
    };
    fetchCar();
  }, [id]);


  const handleChange = (field, value) => {
    let updatedForm = { ...form, [field]: value };
    if (field === "car_number") {
      const onlyNumber = value.slice(-4);
      updatedForm.car_number_only = onlyNumber;
    }
    // 기사명 타이핑에 맞춰 질의어 업데이트
    if (field === "driver") {
      updatedForm.driver_email = "";
      updatedForm.phone = "";
      setDriverQuery(value);
      setShowDriverList(true);
    }
    setForm(updatedForm);
  };

  // ✅ 미배정 기사 불러오기
  const fetchUnassignedDrivers = async () => {
    const company = localStorage.getItem("email");
    if (!company) return;

    const { data, error } = await supabase
      .from("profiles")
      .select("id, driver_name, email, phone, car_number")
      .eq("role", "driver")
      .eq("company", company)
      .or("car_number.is.null,car_number.eq.") // car_number가 NULL 또는 빈 문자열
      .order("driver_name", { ascending: true });

    if (error) {
      console.error("미배정 기사 조회 실패:", error.message);
      setDriverCandidates([]);
    } else {
      setDriverCandidates(data || []);
    }
  };

  // 기사 입력칸 포커스 시 목록 로드 & 표시
  const handleDriverFocus = async () => {
    await fetchUnassignedDrivers();
    setShowDriverList(true);
  };

  // 바깥 클릭 시 닫기
  useEffect(() => {
    const onClickOutside = (e) => {
      if (
        driverListWrapRef.current &&
        !driverListWrapRef.current.contains(e.target) &&
        driverInputRef.current &&
        !driverInputRef.current.contains(e.target)
      ) {
        setShowDriverList(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // 리스트에서 선택
  const selectDriver = (cand) => {
    setForm((prev) => ({
      ...prev,
      driver: cand.driver_name || "",
      driver_email: cand.email || "",
      phone: cand.phone || "",
    }));
    setDriverQuery(cand.driver_name || "");
    setShowDriverList(false);
  };

  // 표시용 필터(클라이언트 측)
  const filteredCandidates = driverCandidates.filter((c) => {
    if (!driverQuery) return true;
    const q = driverQuery.toLowerCase();
    return (
      (c.driver_name || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.phone || "").toLowerCase().includes(q)
    );
  });

  const resolveDriverProfileForSync = async (company) => {
    const email = (form.driver_email || "").trim();
    const driverName = (form.driver || "").trim();

    if (!driverName) return null;

    if (email) {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, driver_name, email, phone, car_number")
        .eq("role", "driver")
        .eq("company", company)
        .eq("email", email)
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        throw new Error("기사 이메일과 일치하는 기사 정보를 찾을 수 없습니다.");
      }
      return data;
    }

    const { data, error } = await supabase
      .from("profiles")
      .select("id, driver_name, email, phone, car_number")
      .eq("role", "driver")
      .eq("company", company)
      .eq("driver_name", driverName);

    if (error) throw error;
    if ((data || []).length === 1) return data[0];
    if ((data || []).length > 1) {
      throw new Error(
        `${driverName} 기사명이 여러 명입니다. 기사 이메일을 입력하거나 목록에서 정확한 기사를 선택해 주세요.`
      );
    }
    throw new Error("기사명과 일치하는 기사 정보를 찾을 수 없습니다.");
  };

  const handleUpdate = async () => {
    try {
      // 숫자/nullable 정리 헬퍼
      const toNullIfEmpty = (v) => (v === "" || v === undefined ? null : v);
      const toNumberOrNull = (v) => {
        if (v === "" || v === null || v === undefined) return null;
        const n = Number(v);
        return Number.isNaN(n) ? null : n;
      };
      const company = localStorage.getItem("email");
      const resolvedDriver = await resolveDriverProfileForSync(company);
      const driverEmail = resolvedDriver?.email || null;
      const driverPhone = form.phone || resolvedDriver?.phone || null;
      const driverName = resolvedDriver?.driver_name || form.driver;

      const updateData = {
        // 문자열 계열: 빈 문자열이면 null 로
        car_number: toNullIfEmpty(form.car_number),
        car_name: toNullIfEmpty(form.car_name),
        driver: toNullIfEmpty(driverName),
        driver_email: driverEmail,
        phone: driverPhone,
        seat: toNullIfEmpty(form.seat),
        seat_detail: toNullIfEmpty(form.seat_detail),
        career: toNullIfEmpty(form.career),
        karaoke_machine: !!form.karaoke_machine,
        electronic_display: !!form.electronic_display,

        // 숫자/금액 계열: 숫자로 변환하고 실패/빈값이면 null
        year: toNumberOrNull(form.year),
        union_fee: toNumberOrNull(form.union_fee),

        // 파생컬럼
        car_number_only: (form.car_number || "").slice(-4) || null,
      };

      const { error } = await supabase
        .from("cars")
        .update(updateData)
        .eq("id", id)
        .eq("company", company);

      if (error) {
        console.error("cars update error:", error);
        showUserAlert("수정 실패: " + (error.message || JSON.stringify(error)));
        return;
      }
      await syncDriverProfileFromCar(supabase, {
        company,
        car: {
          id,
          car_number: updateData.car_number,
          car_number_only: updateData.car_number_only,
          driver: updateData.driver,
          driver_email: updateData.driver_email,
          phone: updateData.phone,
        },
        previousCar: previousCarForSync || {
          id,
          car_number: form.car_number,
          car_number_only: (form.car_number || "").slice(-4) || null,
          driver: prevDriverName,
          driver_email: prevDriverEmail,
          phone: null,
        },
      });

      showUserAlert("차량 정보가 수정되었습니다.");
      navigate("/car-info");
    } catch (err) {
      console.error(err);
      showUserAlert("수정 실패: " + err.message);
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm("정말 삭제하시겠습니까?");
    if (!confirmed) return;
    const company = localStorage.getItem("email") || "";
    const { error } = await supabase
      .from("cars")
      .delete()
      .eq("id", id)
      .eq("company", company);
    if (error) {
      showUserAlert("삭제 실패: " + error.message);
    } else {
      showUserAlert("차량이 삭제되었습니다.");
      navigate("/car-info");
    }
  };

  return (
    <div className="space-y-1">
      <Header />
      <div className="px-4 pt-1 pb-4 space-y-2">
        <h2 className="text-lg font-semibold">차량 정보 수정</h2>
        <Card className="p-2">
          <CardContent className="bg-white">
            <div className="reservation-form-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="reservation-section-title">차량 기본 정보</div>

              <div>
                <label className="block font-semibold mb-1">차량번호</label>
                <Input value={form.car_number} onChange={(e) => handleChange("car_number", e.target.value)} />
              </div>

              <div>
                <label className="block font-semibold mb-1">차명</label>
                <Input value={form.car_name} onChange={(e) => handleChange("car_name", e.target.value)} />
              </div>

              <div>
                <label className="block font-semibold mb-1">년식</label>
                <Input value={form.year} onChange={(e) => handleChange("year", e.target.value)} />
              </div>

              <div>
                <label className="block font-semibold mb-1">승차인원</label>
                <div className="flex gap-2">
                  <select
                    className="w-28 border border-gray-300 rounded px-3 py-2"
                    value={form.seat}
                    onChange={(e) => handleChange("seat", e.target.value)}
                  >
                    <option value="">선택</option>
                    <option value="대형">대형</option>
                    <option value="중형">중형</option>
                    <option value="우등">우등</option>
                  </select>
                  <Input
                    value={form.seat_detail || ""}
                    onChange={(e) => handleChange("seat_detail", e.target.value)}
                    placeholder="예) 45"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="karaoke_machine"
                  type="checkbox"
                  checked={!!form.karaoke_machine}
                  onChange={(e) => handleChange("karaoke_machine", e.target.checked)}
                />
                <label htmlFor="karaoke_machine" className="font-semibold">
                  노래방기기
                </label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="electronic_display"
                  type="checkbox"
                  checked={!!form.electronic_display}
                  onChange={(e) => handleChange("electronic_display", e.target.checked)}
                />
                <label htmlFor="electronic_display" className="font-semibold">
                  전광판
                </label>
              </div>

              <div>
                <label className="block font-semibold mb-1">조합비</label>
                <Input value={form.union_fee || ""} onChange={(e) => handleChange("union_fee", e.target.value)} />
              </div>

              <div className="reservation-section-title">기사 정보</div>

              <div className="relative" ref={driverListWrapRef}>
                <label className="block font-semibold mb-1">기사명</label>
                <Input
                  ref={driverInputRef}
                  value={form.driver}
                  onChange={(e) => handleChange("driver", e.target.value)}
                  onFocus={handleDriverFocus}
                  placeholder="기사명 입력"
                />
                {showDriverList && (
                  <div className="absolute z-10 mt-1 w-full max-h-56 overflow-auto bg-white border rounded shadow-sm">
                    {filteredCandidates.length === 0 ? (
                      <div className="p-2 text-sm text-gray-500">미배정 기사가 없습니다.</div>
                    ) : (
                      <ul className="divide-y">
                        {filteredCandidates.map((c) => (
                          <li
                            key={c.id}
                            className="p-2 hover:bg-gray-100 cursor-pointer text-sm"
                            onClick={() => selectDriver(c)}
                          >
                            <div className="font-medium">{c.driver_name || "(이름 없음)"}</div>
                            <div className="text-gray-600">{c.email || ""}</div>
                            <div className="text-gray-600">{c.phone || ""}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block font-semibold mb-1">기사 이메일</label>
                <Input
                  type="email"
                  value={form.driver_email}
                  onChange={(e) => handleChange("driver_email", e.target.value)}
                  placeholder="driver@example.com"
                />
              </div>

              <div>
                <label className="block font-semibold mb-1">핸드폰</label>
                <Input value={form.phone} onChange={(e) => handleChange("phone", e.target.value)} />
              </div>

              <div className="reservation-section-title">경력사항</div>

              <div className="md:col-span-2 lg:col-span-3">
                <label className="block font-semibold mb-1">경력사항</label>
                <textarea
                  className="w-full border border-gray-300 rounded px-3 py-2 h-24"
                  value={form.career}
                  onChange={(e) => handleChange("career", e.target.value)}
                />
              </div>

              <div className="reservation-action-row">
                <Button onClick={handleUpdate}>수정</Button>
                <Button variant="destructive" onClick={handleDelete}>삭제</Button>
                <Button variant="outline" onClick={() => navigate("/car-info")}>돌아가기</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
